import axios from 'axios';
import { getAppConfig, getExecuteUrl } from '@srichaitra/config';
import { ApiError } from './ApiError';

/**
 * Client half of the hybrid payload encryption.
 *
 * The scheme matches the backend's src/core/payloadCrypto.js exactly: a fresh
 * AES-256-GCM key encrypts each request body, that key is wrapped with the
 * server's RSA public key using OAEP-SHA256, and the reply comes back under the
 * same AES key — which never leaves this tab and is discarded once the response
 * is read.
 *
 * Built on the browser's native WebCrypto rather than a JavaScript crypto
 * library. The primitives are the same ones the server uses, they are
 * constant-time implementations in the browser, and it costs no bundle weight.
 * The one thing to know is that WebCrypto is only available in a secure
 * context — https, or localhost during development. On plain http over a LAN
 * address, crypto.subtle is simply undefined, which is checked for here so the
 * failure names itself instead of surfacing as "cannot read property encrypt".
 *
 * This sits on top of TLS. It is defence in depth — a terminating proxy or a
 * device with a user-installed root CA sees only the envelope — not a
 * replacement for it.
 */

export const ALGORITHM = 'RSA-OAEP-256+A256GCM';

const AES_KEY_BITS = 256;
const IV_BYTES = 12;
/** AES-GCM's authentication tag, which WebCrypto appends to the ciphertext. */
const TAG_BYTES = 16;

let publicKey = null;
let fetchPromise = null;

function subtle() {
  const webcrypto = globalThis.crypto;
  if (!webcrypto?.subtle) {
    throw ApiError.config(
      'Web Crypto is unavailable. The application must be served over https or from localhost.'
    );
  }
  return webcrypto.subtle;
}

/* --- base64 <-> bytes -------------------------------------------------- */

function bytesToBase64(bytes) {
  let binary = '';
  // Chunked: String.fromCharCode(...bytes) overflows the call stack on large
  // payloads, and a document upload would hit that.
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

function base64ToBytes(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Strips the PEM armour and returns the DER bytes inside. */
function pemToDer(pem) {
  const body = pem
    .replace(/-----BEGIN [^-]+-----/, '')
    .replace(/-----END [^-]+-----/, '')
    .replace(/\s+/g, '');
  return base64ToBytes(body);
}

/* --- key handling ------------------------------------------------------ */

/**
 * Fetches the server's public key once and keeps it for the life of the tab.
 *
 * Uses a bare axios call rather than the shared client on purpose: the shared
 * client encrypts everything it sends, and this is the one request that cannot
 * be — it is how the key to encrypt with is obtained. Going direct also keeps
 * this module out of an import cycle with the interceptor.
 *
 * Held in memory only. The key is cheap to re-fetch, and a copy in storage is
 * one more thing that can go stale against a rotated server key.
 */
export function loadPublicKey() {
  if (publicKey) return Promise.resolve(publicKey);
  if (fetchPromise) return fetchPromise;

  fetchPromise = (async () => {
    try {
      const { timeoutMs } = getAppConfig().api;
      const response = await axios.post(
        getExecuteUrl(),
        { action: 'system.publicKey' },
        { timeout: timeoutMs, headers: { 'Content-Type': 'application/json' } }
      );

      const result = response.data?.data;
      if (result?.algorithm !== ALGORITHM) {
        throw ApiError.config(`Server expects "${result?.algorithm}", which this client cannot produce`);
      }

      publicKey = await subtle().importKey(
        'spki',
        pemToDer(result.publicKey),
        { name: 'RSA-OAEP', hash: 'SHA-256' },
        false,
        ['encrypt']
      );

      return publicKey;
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
}

/** Drops the cached key so the next call re-fetches — used when the server rejects our wrapping. */
export function resetPublicKey() {
  publicKey = null;
}

/* --- the envelope ------------------------------------------------------ */

/**
 * Encrypts a request body.
 *
 * Returns the envelope to post plus the AES key needed to read the reply, which
 * the interceptor keeps on the request config until the response arrives.
 */
export async function encryptBody(body) {
  const rsaKey = await loadPublicKey();
  const crypto = subtle();

  const aesKey = await crypto.generateKey({ name: 'AES-GCM', length: AES_KEY_BITS }, true, [
    'encrypt',
    'decrypt'
  ]);

  const iv = globalThis.crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const plaintext = new TextEncoder().encode(JSON.stringify(body));

  const sealed = new Uint8Array(await crypto.encrypt({ name: 'AES-GCM', iv }, aesKey, plaintext));

  // WebCrypto returns ciphertext||tag in one buffer; the server expects them as
  // separate fields, because Node's crypto API keeps the tag separate.
  const ciphertext = sealed.subarray(0, sealed.length - TAG_BYTES);
  const tag = sealed.subarray(sealed.length - TAG_BYTES);

  const rawAesKey = await crypto.exportKey('raw', aesKey);
  const wrappedKey = new Uint8Array(await crypto.encrypt({ name: 'RSA-OAEP' }, rsaKey, rawAesKey));

  return {
    envelope: {
      enc: ALGORITHM,
      key: bytesToBase64(wrappedKey),
      iv: bytesToBase64(iv),
      tag: bytesToBase64(tag),
      data: bytesToBase64(ciphertext)
    },
    aesKey
  };
}

/** Whether a response body is an encrypted envelope rather than a plain one. */
export function isEncryptedEnvelope(body) {
  return Boolean(
    body &&
      typeof body === 'object' &&
      typeof body.enc === 'string' &&
      typeof body.iv === 'string' &&
      typeof body.tag === 'string' &&
      typeof body.data === 'string'
  );
}

/**
 * Decrypts a response with the AES key from the matching request.
 *
 * A failed authentication tag rejects rather than returning partial output: the
 * reply was altered in transit or answered under a different key, and neither
 * is something to parse and act on.
 */
export async function decryptBody(aesKey, envelope) {
  const iv = base64ToBytes(envelope.iv);
  const ciphertext = base64ToBytes(envelope.data);
  const tag = base64ToBytes(envelope.tag);

  // Reassemble the layout WebCrypto expects: ciphertext followed by the tag.
  const sealed = new Uint8Array(ciphertext.length + tag.length);
  sealed.set(ciphertext, 0);
  sealed.set(tag, ciphertext.length);

  let plaintext;
  try {
    plaintext = await subtle().decrypt({ name: 'AES-GCM', iv }, aesKey, sealed);
  } catch {
    throw new ApiError('PAYLOAD_DECRYPT_FAILED', 'Response failed its integrity check', 0);
  }

  return JSON.parse(new TextDecoder().decode(plaintext));
}
